import { TWidget } from 'widgets/Widget';
import { RefElement } from 'widgets/toolbox/RefElement';

const keyCode = Object.freeze({
    END: 'End',
    HOME: 'Home',
    LEFT: 'ArrowLeft',
    RIGHT: 'ArrowRight',
    UP: 'ArrowUp',
    DOWN: 'ArrowDown'
});

/**
 * @param Widget Base widget for extending
 * @returns ProductFocusManagementMixin class
 */
function ProductFocusManagementMixinClassCreator(Widget: TWidget) {
    /**
     * @category widgets
     * @subcategory global
     * @class ProductFocusManagementMixin
     * @augments Widget
     * @classdesc Base ProductFocusManagementMixin implementation.
     */
    class ProductFocusManagementMixin extends Widget {
        buttons: NodeListOf<HTMLButtonElement> | null = null;

        prefs() {
            return {
                selector: false,
                tabs: false,
                ...super.prefs()
            };
        }

        init() {
            const container = this.ref('self').get();

            if (container) {
                this.getButtons();
                this.initializeTabIndex();

                this.eventBus().on('product.variant.changed', 'initializeTabIndex');
                this.eventBus().on('product.size.changed', 'initializeTabIndex');
            }
        }

        getButtons() {
            const container = this.ref('self').get();
            const selector = this.prefs().selector ? this.prefs().selector + ' [role="radio"]' : '[role="radio"]';

            if (container) {
                this.buttons = container.querySelectorAll(selector);
            }
        }

        /**
         * @description Render the variation controls and preserve focus when the active radio is replaced.
         * @param templateRefId Template reference ID
         * @param data Template data
         * @param renderTo Element receiving the rendered markup
         * @param strToRender Pre-rendered markup
         * @returns Promise resolved after rendering and focus restoration
         */
        render(
            templateRefId = 'template',
            data: Record<string, unknown> = {},
            renderTo: RefElement = this.ref('self'),
            strToRender = ''
        ): Promise<void> {
            const container = this.ref('self').get();
            const activeElement = document.activeElement;
            const activeButtonId =
                activeElement instanceof HTMLButtonElement && activeElement.getAttribute('role') === 'radio' && container?.contains(activeElement)
                    ? activeElement.id
                    : undefined;

            return super.render(templateRefId, data, renderTo, strToRender).then(() => {
                this.getButtons();
                this.initializeTabIndex();

                if (!activeButtonId || !this.buttons) {
                    return;
                }

                const replacementButton = Array.from(this.buttons).find(button => button.id === activeButtonId);

                if (replacementButton) {
                    this.setActiveButton(replacementButton, true);
                }
            });
        }

        /**
         * @description Keydown Event handler
         * @param _ Source of keydown event
         * @param event  Event object
         */
        handleKeydown(_: HTMLElement, event: KeyboardEvent) {
            let preventEventActions = false;

            switch (event.code) {
                case keyCode.HOME:
                    this.setFocusToFirstRadio();
                    preventEventActions = true;
                    break;
                case keyCode.END:
                    this.setFocusToLastRadio();
                    preventEventActions = true;
                    break;
                case keyCode.LEFT:
                case keyCode.UP:
                    this.setFocusToPreviousRadio();
                    preventEventActions = true;
                    break;
                case keyCode.RIGHT:
                case keyCode.DOWN:
                    this.setFocusToNextRadio();
                    preventEventActions = true;
                    break;
                default:
                    break;
            }

            if (preventEventActions) {
                event.preventDefault();
                event.stopPropagation();
            }
        }

        /**
         * @description Set focus to the first radio button
         */
        setFocusToFirstRadio(): void {
            if (this.buttons && this.buttons.length > 0) {
                this.setActiveButton(this.buttons[0]);
            }
        }

        /**
         * @description Set focus to the last radio button
         */
        setFocusToLastRadio(): void {
            if (this.buttons && this.buttons.length > 0) {
                this.setActiveButton(this.buttons[this.buttons.length - 1]);
            }
        }

        /**
         * @description Set focus to the previous radio button
         */
        setFocusToPreviousRadio(): void {
            if (!this.buttons || this.buttons.length === 0) {
                return;
            }

            const currentIndex = Array.from(this.buttons).findIndex(button => button === document.activeElement);

            if (currentIndex > 0) {
                this.setActiveButton(this.buttons[currentIndex - 1]);
            } else if (currentIndex === 0) {
                // Wrap to last button
                this.setActiveButton(this.buttons[this.buttons.length - 1]);
            }
        }

        /**
         * @description Set focus to the next radio button
         */
        setFocusToNextRadio(): void {
            if (!this.buttons || this.buttons.length === 0) {
                return;
            }

            const currentIndex = Array.from(this.buttons).findIndex(button => button === document.activeElement);

            if (currentIndex >= 0 && currentIndex < this.buttons.length - 1) {
                this.setActiveButton(this.buttons[currentIndex + 1]);
            } else if (currentIndex === this.buttons.length - 1) {
                // Wrap to first button
                this.setActiveButton(this.buttons[0]);
            }
        }

        /**
         * @description Initialize tabindex for radio button group behavior
         * Only one button should be tabbable at a time
         */
        initializeTabIndex(): void {
            if (this.prefs().tabs) {
                this.getButtons();
            }

            if (!this.buttons || this.buttons.length === 0) {
                return;
            }

            const buttons = Array.from(this.buttons);
            const activeButton = buttons.find(button => button === document.activeElement);
            const selectedButton = buttons.find(button => button.getAttribute('aria-checked') === 'true');
            const tabbableButton = activeButton || selectedButton || buttons[0];

            buttons.forEach(button => {
                button.setAttribute('tabindex', button === tabbableButton ? '0' : '-1');
            });
        }

        /**
         * @description Set a button as the active (tabbable) button and focus it
         * @param button The button to make active
         * @param preventScroll Whether focusing should preserve the current scroll position
         */
        setActiveButton(button: HTMLButtonElement, preventScroll = false): void {
            if (!this.buttons) {
                return;
            }

            // Set tabindex for all buttons
            Array.from(this.buttons).forEach(btn => {
                if (btn === button) {
                    btn.setAttribute('tabindex', '0');
                    btn.focus({ preventScroll });
                } else {
                    btn.setAttribute('tabindex', '-1');
                }
            });
        }
    }

    return ProductFocusManagementMixin;
}

export type TProductFocusManagementMixin = ReturnType<typeof ProductFocusManagementMixinClassCreator>;

export type TProductFocusManagementMixinInstance = InstanceType<TProductFocusManagementMixin>;

export default ProductFocusManagementMixinClassCreator;
